import flatMap from "lodash/flatMap";
import reduce from "lodash/reduce";
import uniq from "lodash/uniq";
import { CustomFieldChangeDecorated } from "PFApp/components/changelog/components/custom_fields_changelog/custom_field_changelog";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCustomValues } from "PFCore/hooks/queries/custom_values/use_custom_values";
import { ActivityConnectionMetadataValue, ActivityConnectionsEvent } from "PFTypes";

type UseConnectionsMetadataToChangelogReturn = {
  changelog: Record<string, CustomFieldChangeDecorated>;
  isFetching: boolean;
};

/**
 * Transforms activity_connections_updated metadata (new format) into
 * structure suitable for rendering with CustomFieldsChangelog component.
 *
 * Returns null when metadata lacks before/after — signals caller to fall back to legacy rendering.
 */
export const useConnectionsMetadataToChangelog = (
  metadata: ActivityConnectionsEvent["metadata"]
): UseConnectionsMetadataToChangelogReturn | null => {
  const { before, after, rankable = [], matchable = [] } = metadata;
  const { getCustomTypeById } = useCustomTypes();

  const beforeData = before ?? {};
  const afterData = after ?? {};
  const allCustomValueIds = flatMap([...Object.values(beforeData), ...Object.values(afterData)], (values) =>
    values.map(({ customValueId }) => customValueId)
  );

  const { data: customValues, isFetching } = useCustomValues(
    {
      ids: allCustomValueIds
    },
    { enabled: (before || after) && allCustomValueIds.length > 0 }
  );

  // TODO: [SP-9670] Remove legacy history events support
  if (!before && !after) {
    return null;
  }

  const allFieldIds = uniq([...Object.keys(beforeData), ...Object.keys(afterData)]);
  const customValueNames = reduce(
    customValues?.entries,
    (acc, { id, text, value }) => ({ ...acc, [id]: String(text || value) }),
    {} as Record<string, string>
  );

  const changelog = reduce(
    allFieldIds,
    (acc, fieldId) => {
      const customType = getCustomTypeById(Number(fieldId));
      const customFieldName = customType?.display_as || customType?.name || fieldId;

      const isMatchable = matchable.some((id) => String(id) === fieldId);
      const isRankable = rankable.some((id) => String(id) === fieldId);

      const withCustomValueName = (values: ActivityConnectionMetadataValue[]) =>
        values.map((item) => ({
          ...item,
          customValue: customValueNames[item.customValueId] ?? String(item.customValueId)
        }));

      acc[fieldId] = {
        before: withCustomValueName(beforeData[fieldId] ?? []),
        after: withCustomValueName(afterData[fieldId] ?? []),
        matchable: isMatchable,
        rankable: isRankable,
        customFieldName
      };

      return acc;
    },
    {} as Record<string, CustomFieldChangeDecorated>
  );

  return { changelog, isFetching };
};
